import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  type LucideIcon,
} from "lucide-react";
import { useTranslate } from "ra-core";
import { useRef, type KeyboardEvent, type ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { safeHref } from "./markdown";
import { priorityLabelKey } from "./labels";
import type { MarketPriority } from "./types";

const PRIORITY_CLASS: Record<MarketPriority, string> = {
  alta: "bg-red-500/12 text-red-700 ring-red-500/25 dark:bg-red-400/15 dark:text-red-300 dark:ring-red-400/30",
  media:
    "bg-amber-500/14 text-amber-800 ring-amber-500/25 dark:bg-amber-400/15 dark:text-amber-200 dark:ring-amber-400/30",
  baja: "bg-slate-500/10 text-slate-700 ring-slate-500/20 dark:bg-slate-300/12 dark:text-slate-300 dark:ring-slate-300/25",
};

const PRIORITY_DOT: Record<MarketPriority, string> = {
  alta: "bg-red-500",
  media: "bg-amber-500",
  baja: "bg-slate-400",
};

export const PriorityBadge = ({
  priority,
  className,
}: {
  priority: MarketPriority;
  className?: string;
}) => {
  const translate = useTranslate();
  const known = PRIORITY_CLASS[priority] ? priority : "media";
  return (
    <span
      className={cn(
        "inline-flex h-6 shrink-0 items-center gap-1.5 rounded-full px-2.5 text-xs font-semibold ring-1 ring-inset",
        PRIORITY_CLASS[known],
        className,
      )}
    >
      <span
        aria-hidden
        className={cn("size-1.5 rounded-full", PRIORITY_DOT[known])}
      />
      <span className="sr-only">{translate("market.priority.label")}: </span>
      {translate(priorityLabelKey(known))}
    </span>
  );
};

/** Small pill; a long label is cut with an ellipsis, never widening its row. */
export const Chip = ({ children }: { children: ReactNode }) => (
  <span className="inline-flex h-6 max-w-full min-w-0 items-center rounded-full bg-muted px-2.5 text-xs font-medium text-muted-foreground">
    <span className="truncate">{children}</span>
  </span>
);

/** Small uppercase label above a card section. */
export const SectionLabel = ({
  children,
  icon: Icon,
}: {
  children: ReactNode;
  icon?: LucideIcon;
}) => (
  <p className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.06em] text-muted-foreground uppercase">
    {Icon ? <Icon aria-hidden className="size-3.5" /> : null}
    {children}
  </p>
);

export const ExternalAnchor = ({
  href,
  children,
  className,
}: {
  href: string | null | undefined;
  children: ReactNode;
  className?: string;
}) => {
  const safe = href ? safeHref(href) : null;
  if (!safe)
    return (
      <span className={cn("inline-block max-w-full truncate", className)}>
        {children}
      </span>
    );
  return (
    <a
      href={safe}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        "inline-flex max-w-full min-w-0 items-center gap-1 font-medium text-primary underline-offset-2 hover:underline",
        className,
      )}
    >
      <span className="truncate">{children}</span>
      <ExternalLink aria-hidden className="size-3.5 shrink-0 opacity-70" />
    </a>
  );
};

export const EmptyState = ({
  icon: Icon,
  title,
  hint,
  action,
}: {
  icon: LucideIcon;
  title: string;
  hint?: string;
  action?: ReactNode;
}) => (
  <div
    role="status"
    className="mi-card mi-rise flex flex-col items-center gap-3 px-6 py-10 text-center"
  >
    <span
      aria-hidden
      className="flex size-12 items-center justify-center rounded-2xl bg-muted text-muted-foreground"
    >
      <Icon className="size-6" />
    </span>
    <div className="space-y-1">
      <p className="text-base font-semibold">{title}</p>
      {hint ? (
        <p className="text-sm text-balance text-muted-foreground">{hint}</p>
      ) : null}
    </div>
    {action}
  </div>
);

/** The date heading placed between the arrows on a phone. */
export interface StepperHeading {
  id: string;
  /** Short visible text, e.g. "mié, 4 nov". */
  text: string;
  /** Full accessible name of the heading, e.g. "miércoles, 4 de noviembre de 2026". */
  label: string;
}

interface StepperProps {
  onPrevious?: () => void;
  onNext?: () => void;
  onLatest?: () => void;
  previousLabel: string;
  nextLabel: string;
  /** ISO day shown by the native date picker, when there is one. */
  pickerValue?: string;
  onPick?: (date: string) => void;
  /**
   * Compact phone layout: the date heading sits between the arrows (and opens
   * the picker), so the whole navigation is one row.
   */
  heading?: StepperHeading;
}

/**
 * Previous / next (by available record) arrows, an optional native date
 * picker and the "Último" shortcut. Disabled arrows mean there is no older or
 * newer record.
 */
export const DateStepper = ({
  onPrevious,
  onNext,
  onLatest,
  previousLabel,
  nextLabel,
  pickerValue,
  onPick,
  heading,
}: StepperProps) => {
  const translate = useTranslate();
  const picker = useRef<HTMLInputElement>(null);
  const pickLabel = translate("market.nav.pick_date");

  const openPicker = () => {
    const input = picker.current;
    if (!input) return;
    try {
      input.showPicker();
    } catch {
      input.focus();
    }
  };

  const round =
    "liquid-glass glass-press size-10 shrink-0 rounded-full p-0 text-[color:var(--glass-foreground)] disabled:opacity-40 md:size-9";

  const previous = (
    <Button
      type="button"
      variant="ghost"
      className={round}
      onClick={onPrevious}
      disabled={!onPrevious}
      aria-label={previousLabel}
      title={previousLabel}
    >
      <ChevronLeft className="size-5" />
    </Button>
  );
  const next = (
    <Button
      type="button"
      variant="ghost"
      className={round}
      onClick={onNext}
      disabled={!onNext}
      aria-label={nextLabel}
      title={nextLabel}
    >
      <ChevronRight className="size-5" />
    </Button>
  );
  const input = onPick ? (
    <input
      ref={picker}
      type="date"
      tabIndex={-1}
      aria-hidden
      value={pickerValue ?? ""}
      onChange={(event) => {
        if (event.target.value) onPick(event.target.value);
      }}
      className="mi-date-proxy pointer-events-none size-full max-w-full min-w-0 opacity-0"
    />
  ) : null;
  const latest = (
    <Button
      type="button"
      variant="ghost"
      onClick={onLatest}
      disabled={!onLatest}
      className="liquid-glass glass-press ml-auto h-10 shrink-0 rounded-full px-4 text-sm font-semibold text-[color:var(--glass-foreground)] disabled:opacity-40 md:h-9"
    >
      {translate("market.nav.latest")}
    </Button>
  );

  if (heading)
    return (
      <div className="flex items-center gap-2">
        {previous}
        <h2
          id={heading.id}
          aria-label={heading.label}
          className="relative flex min-w-0 flex-1 justify-center"
        >
          {onPick ? (
            <button
              type="button"
              onClick={openPicker}
              aria-label={`${pickLabel}: ${heading.label}`}
              title={pickLabel}
              className="liquid-glass glass-press flex h-10 max-w-full min-w-0 items-center gap-1.5 rounded-full px-3.5 text-[15px] font-semibold text-[color:var(--glass-foreground)]"
            >
              <CalendarDays
                aria-hidden
                className="size-4 shrink-0 opacity-70"
              />
              <span className="truncate">{heading.text}</span>
            </button>
          ) : (
            <span className="truncate text-[15px] font-semibold first-letter:uppercase">
              {heading.text}
            </span>
          )}
          {input}
        </h2>
        {next}
        {latest}
      </div>
    );

  return (
    <div className="flex items-center gap-2">
      {previous}
      {next}
      {onPick ? (
        <span className="relative flex size-10 shrink-0 md:size-9">
          <Button
            type="button"
            variant="ghost"
            className={round}
            onClick={openPicker}
            aria-label={pickLabel}
            title={pickLabel}
          >
            <CalendarDays className="size-[1.125rem]" />
          </Button>
          {input}
        </span>
      ) : null}
      {latest}
    </div>
  );
};

export const LoadingCards = ({ count = 3 }: { count?: number }) => {
  const translate = useTranslate();
  return (
    <div className="space-y-3" aria-busy="true">
      <span className="sr-only">{translate("market.loading")}</span>
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="mi-card h-40 animate-pulse opacity-60" />
      ))}
    </div>
  );
};

interface PillGroupProps<T extends string> {
  label: string;
  value: T;
  options: readonly { value: T; label: string }[];
  onChange: (value: T) => void;
}

/**
 * Single-choice filter as a row of glass pills that scrolls sideways on a
 * phone (long labels never truncate). Radio-group keyboard model.
 */
export const PillGroup = <T extends string>({
  label,
  value,
  options,
  onChange,
}: PillGroupProps<T>) => {
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);
  const index = Math.max(
    0,
    options.findIndex((option) => option.value === value),
  );

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step =
      event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
    if (!step) return;
    event.preventDefault();
    const next = (index + step + options.length) % options.length;
    onChange(options[next].value);
    buttons.current[next]?.focus();
    buttons.current[next]?.scrollIntoView({
      inline: "nearest",
      block: "nearest",
    });
  };

  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="mi-pills"
      onKeyDown={onKeyDown}
    >
      {options.map((option, position) => {
        const checked = position === index;
        return (
          <button
            key={option.value}
            ref={(node) => {
              buttons.current[position] = node;
            }}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={checked ? 0 : -1}
            onClick={() => onChange(option.value)}
            className="mi-pill liquid-glass outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};
